import PropTypes from 'prop-types'
import { createRef, PureComponent } from 'react'
import SignaturePad from 'signature_pad'

import { cn } from '@repo/utils'

import getCroppedImg from '../ImageCrop/cropImage'
import SimpleButton from '../SimpleButton'

import styles from './index.css'

const RESTORE_CONTENT_DEBOUNCE_MS = 250

class SignatureZone extends PureComponent {
  constructor(props) {
    super(props)

    this.state = {
      containerWidth: 0,
      isSigning: false,
    }

    this.restoreContentTimeout = null
    this.canvas = createRef()
    this.el = createRef()
    this.signaturePad = null
  }

  componentDidMount() {
    if (this.canvas.current) {
      this.signaturePad = new SignaturePad(this.canvas.current)
      if (this.signaturePad?.addEventListener) {
        this.signaturePad.addEventListener('endStroke', this.onChange)
      }
    }

    window.addEventListener('resize', this.resize)
    this.resize()
  }

  componentDidUpdate(prevProps) {
    if (prevProps.signWidth !== this.props.signWidth || prevProps.signHeight !== this.props.signHeight) {
      this.restoreCanvasContent()
    }
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.resize)
    clearTimeout(this.restoreContentTimeout)
    if (this.signaturePad?.removeEventListener) {
      this.signaturePad.removeEventListener('endStroke', this.onChange)
      this.signaturePad.off()
    }
  }

  // to mock async actions only
  /* istanbul ignore next */

  getCroppedImg = (...args) => getCroppedImg(...args)

  onCancel = () => {
    this.signaturePad.clear()
    this.props.onChange(null)
    this.setState({ isSigning: false })
  }

  onChange = () => {
    this.props.onChange(this.signaturePad.toDataURL())
    this.setState({ isSigning: true })
  }

  onSign = () =>
    this.canvas.current.toBlob(async (file) => {
      let croppedImage = null
      let croppedImageBlob = null
      try {
        // turn it into a jpeg with white bg
        ;[croppedImage, croppedImageBlob] = await this.getCroppedImg({
          imageSrc: URL.createObjectURL(file),
          pixelCrop: {
            height: this.canvas.current.height,
            width: this.canvas.current.width,
            x: 0,
            y: 0,
          },
        })
      } catch (e) {
        /* istanbul ignore next */
        // eslint-disable-next-line no-console
        console.error(e)
      }
      this.props.onSign(croppedImage, croppedImageBlob)
    }, 'image/png')

  resize = () => {
    clearTimeout(this.restoreContentTimeout)
    this.setState(() => {
      clearTimeout(this.restoreContentTimeout)
      this.restoreContentTimeout = setTimeout(this.restoreCanvasContent, RESTORE_CONTENT_DEBOUNCE_MS)
      return { containerWidth: Number(this.el.current?.offsetWidth) || 0 }
    })
  }

  restoreCanvasContent = async () => {
    const width = Number(this.el.current?.offsetWidth) || 0
    const ratio = Math.max(window.devicePixelRatio || 1, 1)
    const { signHeight, signWidth } = this.props
    const aspect = signWidth / signHeight
    const height = width / aspect

    const canvas = this.canvas.current
    const data = this.signaturePad?.toDataURL()

    // data content is devicePixelRatio scaled
    canvas.width = width * ratio
    canvas.height = height * ratio
    await this.signaturePad?.fromDataURL(data)

    // restore canvas to container size
    canvas.width = width
    canvas.height = height
  }

  renderButtons() {
    const { isSigning } = this.state
    return (
      <div className={styles.buttons}>
        {isSigning && (
          <>
            <SimpleButton
              ariaLabel="Eliminar Firma"
              icon="trash"
              itemsDirection="rtl"
              onClick={this.onCancel}
              secondary
            >
              Borrar
            </SimpleButton>
            <SimpleButton ariaLabel="Aprobar Firma" icon="checkmark" onClick={this.onSign} primary>
              Seleccionar
            </SimpleButton>
          </>
        )}
      </div>
    )
  }

  render() {
    const { className, signHeight, signWidth } = this.props
    const { containerWidth } = this.state
    const aspect = signWidth / signHeight
    const containerHeight = containerWidth / aspect
    const style = { height: containerHeight, width: containerWidth }

    return (
      <div className={cn(styles.wrapper, className)} ref={this.el}>
        <div className={styles.content} style={style}>
          <canvas className={styles.canvas} ref={this.canvas} />
        </div>
        <div>{this.renderButtons()}</div>
      </div>
    )
  }
}

SignatureZone.propTypes = {
  onSign: PropTypes.func.isRequired,
  signHeight: PropTypes.number.isRequired,
  signWidth: PropTypes.number.isRequired,

  className: PropTypes.string,
  onChange: PropTypes.func,
}

SignatureZone.defaultProps = {
  className: '',
  onChange: Function.prototype,
}

export default SignatureZone
